<script setup lang="ts">
import { useLexicalComposer } from "lexical-vue";
import { computed, ref } from "vue";

import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import {
  ChevronDownIcon,
  CodeBlockIcon,
  HorizontalRuleIcon,
  LargeHeaderIcon,
  MediumHeaderIcon,
  QuoteBlockIcon,
  SmallHeaderIcon,
  StandardTextIcon,
  TableChartIcon,
} from "~/icons";
import { CommandId, DropdownMenuItemKind } from "~/shared/enums";
import type { TextBlockType } from "~/shared/types";
import { when } from "~/utils/wait";

import { EVENT_INSERT_HORIZONTAL_RULE } from "../const";
import { createTextBlockNode, insertTable } from "../utils";

const props = defineProps<{
  blockType: TextBlockType;
}>();

const editor = useLexicalComposer();

const menu = ref<InstanceType<typeof DropdownMenu> | null>(null);
const isMenuOpen = computed(() => menu.value?.isOpen ?? false);

const formatBlock = async (blockType: TextBlockType) => {
  if (props.blockType === blockType) {
    return;
  }

  await when(computed(() => !menu.value?.isOpen));
  editor.getRootElement()?.focus({ preventScroll: true });
  editor.update(() => {
    createTextBlockNode(blockType);
  });

  editor.focus();
};

const insertHorizontalRule = async () => {
  editor.getRootElement()?.focus({ preventScroll: true });
  editor.dispatchCommand(EVENT_INSERT_HORIZONTAL_RULE, null);
  await when(computed(() => !menu.value?.isOpen));
  editor.focus();
};

const insertTableInner = async () => {
  editor.getRootElement()?.focus({ preventScroll: true });
  insertTable(editor);
  await when(computed(() => !menu.value?.isOpen));
  editor.focus();
};

const blockTypeToBlockName: Record<string, string> = {
  paragraph: "Standard text",
  h1: "Large header",
  h2: "Medium header",
  h3: "Small header",
  code: "Code",
  quote: "Quote",
  horizontalRule: "Horizontal rule",
  table: "Table",
};

const textStyleDropdownSections = computed(() => [
  {
    title: "Text style",
    items: [
      {
        title: "Standard text",
        kind: DropdownMenuItemKind.BUTTON,
        icon: StandardTextIcon,
        disabled: props.blockType === "paragraph",
        commandId: CommandId.EDITOR_SET_STANDARD_TEXT,
        onClick: () => formatBlock("paragraph"),
      },
      {
        title: "Large header",
        kind: DropdownMenuItemKind.BUTTON,
        icon: LargeHeaderIcon,
        disabled: props.blockType === "h1",
        commandId: CommandId.EDITOR_SET_LARGE_HEADER,
        onClick: () => formatBlock("h1"),
      },
      {
        title: "Medium header",
        kind: DropdownMenuItemKind.BUTTON,
        icon: MediumHeaderIcon,
        disabled: props.blockType === "h2",
        commandId: CommandId.EDITOR_SET_MEDIUM_HEADER,
        onClick: () => formatBlock("h2"),
      },
      {
        title: "Small header",
        kind: DropdownMenuItemKind.BUTTON,
        icon: SmallHeaderIcon,
        disabled: props.blockType === "h3",
        commandId: CommandId.EDITOR_SET_SMALL_HEADER,
        onClick: () => formatBlock("h3"),
      },
      {
        title: "Code block",
        kind: DropdownMenuItemKind.BUTTON,
        icon: CodeBlockIcon,
        disabled: props.blockType === "code",
        commandId: CommandId.EDITOR_SET_CODE_BLOCK,
        onClick: () => formatBlock("code"),
      },
      {
        title: "Quote",
        kind: DropdownMenuItemKind.BUTTON,
        icon: QuoteBlockIcon,
        disabled: props.blockType === "quote",
        commandId: CommandId.EDITOR_SET_QUOTE,
        onClick: () => formatBlock("quote"),
      },
      // TODO toggle block add back
      // {
      //   title: "Toggle block",
      //   kind: DropdownMenuItemKind.BUTTON,
      //   icon: ToggleSectionIcon,
      //   commandId: CommandId.EDITOR_SET_TOGGLE_BLOCK,
      //   onClick: () => editor.dispatchCommand(EVENT_INSERT_TOGGLE_COMMAND, null),
      // },
      {
        title: "Horizontal rule",
        kind: DropdownMenuItemKind.BUTTON,
        icon: HorizontalRuleIcon,
        commandId: CommandId.EDITOR_SET_HORIZONTAL_RULE,
        onClick: insertHorizontalRule,
      },
      {
        title: "Table",
        kind: DropdownMenuItemKind.BUTTON,
        icon: TableChartIcon,
        commandId: CommandId.EDITOR_SET_TABLE,
        onClick: insertTableInner,
      },
    ],
  },
]);
</script>

<template>
  <DropdownMenu ref="menu" :sections="textStyleDropdownSections" :distance="8">
    <button
      type="button"
      class="flex w-32 items-center justify-between gap-1 rounded-sm px-1 py-0.5 text-sm on:bg-md"
      :data-on="isMenuOpen">
      <span class="truncate">{{ blockTypeToBlockName[blockType] ?? blockTypeToBlockName.paragraph }}</span>
      <ChevronDownIcon class="icon-sm text-vlt" />
    </button>
  </DropdownMenu>
</template>
